import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const TEAL   = '#14b8a6';
const BORD   = '#2a3040';
const MUTED  = '#6b7280';
const DARK   = '#0f1117';
const RED    = '#ef4444';
const AMBER  = '#f59e0b';
const GREEN  = '#22c55e';

function fmtDate(ds) {
  return new Date(ds + 'T12:00:00').toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtRangeLabel(c) {
  if (c.date_from === c.date_to) return fmtDate(c.date_from);
  return `${fmtDate(c.date_from)} – ${fmtDate(c.date_to)}`;
}

function dayCount(c) {
  return Math.round((new Date(c.date_to + 'T12:00:00') - new Date(c.date_from + 'T12:00:00')) / 86400000) + 1;
}

function classifyItem(item, threshold) {
  if (item.daysBeforeDeadline === null) return 'green';
  if (item.daysBeforeDeadline < 0)      return 'red';
  if (item.daysBeforeDeadline < threshold) return 'amber';
  return 'green';
}

// ── Impact row ────────────────────────────────────────────────────────────────

function ImpactRow({ item, cls, onAccept, navigate }) {
  const isRed   = cls === 'red';
  const isAmber = cls === 'amber';
  const accentColor = isRed ? RED : isAmber ? AMBER : GREEN;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${BORD}` }}>
      <div style={{ width: '3px', borderRadius: '2px', alignSelf: 'stretch', background: accentColor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#f0f2f5' }}>{item.partName}</span>
          {item.dayCount > 1 && (
            <span style={{ fontSize: '11px', color: MUTED }}>{item.dayCount}-day plan</span>
          )}
        </div>
        <div style={{ fontSize: '12px', color: MUTED, marginTop: '3px', lineHeight: 1.5 }}>
          Finishes <span style={{ color: '#c9cdd4' }}>{fmtDate(item.newLastDate)}</span>
          {item.oldLastDate !== item.newLastDate && (
            <span style={{ marginLeft: '6px', opacity: 0.6 }}>← was {fmtDate(item.oldLastDate)}</span>
          )}
        </div>
        {item.deadline && (
          <div style={{ fontSize: '12px', marginTop: '2px' }}>
            <span style={{ color: MUTED }}>Deadline </span>
            <span style={{ color: '#c9cdd4' }}>{fmtDate(item.deadline)}</span>
            <span style={{ marginLeft: '8px', fontWeight: '600', color: accentColor }}>
              {item.daysBeforeDeadline < 0
                ? `${Math.abs(item.daysBeforeDeadline)} day${Math.abs(item.daysBeforeDeadline) !== 1 ? 's' : ''} late`
                : `${item.daysBeforeDeadline} day${item.daysBeforeDeadline !== 1 ? 's' : ''} to spare`}
            </span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
        {isAmber && (
          <button onClick={() => onAccept(item)}
            style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: '6px', color: AMBER, fontSize: '12px', fontWeight: '600', padding: '5px 12px', cursor: 'pointer' }}>
            Accept
          </button>
        )}
        <button onClick={() => navigate('/admin/schedule')}
          style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORD}`, borderRadius: '6px', color: '#c9cdd4', fontSize: '12px', padding: '5px 12px', cursor: 'pointer' }}>
          Go to Schedule →
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Closures() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const { isDirector } = useAuth();

  const [closures, setClosures]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [reason, setReason]             = useState('');
  const [saving, setSaving]             = useState(false);
  const [formError, setFormError]       = useState(null);

  // Impact state
  const [impactResult, setImpactResult] = useState(null); // { closure, lostDays, impact[] }
  const [warnThreshold, setWarnThreshold] = useState(3);
  const [accepted, setAccepted]         = useState(new Set()); // keys of amber items user accepted

  function load() {
    api.get('/api/v1/closures').then(r => { setClosures(r.data); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  function handleFromChange(v) {
    setDateFrom(v);
    if (dateTo && dateTo < v) setDateTo(v);
  }

  function add(e) {
    e.preventDefault();
    if (!dateFrom || !dateTo) return;
    setSaving(true);
    setFormError(null);
    setImpactResult(null);
    setAccepted(new Set());
    api.post('/api/v1/closures', { date_from: dateFrom, date_to: dateTo, reason: reason.trim() || null })
      .then(r => {
        setDateFrom(''); setDateTo(''); setReason('');
        setSaving(false);
        setImpactResult(r.data);
        load();
      })
      .catch(err => { setFormError(err.response?.data?.error || 'Save failed'); setSaving(false); });
  }

  function acceptItem(item) {
    const key = item.planId || `solo-${item.repJobId}`;
    setAccepted(prev => new Set([...prev, key]));
  }

  const today = new Date().toISOString().split('T')[0];
  const upcoming = closures.filter(c => c.date_to >= today);
  const past     = closures.filter(c => c.date_to < today);

  // Classify impact items
  const classified = impactResult?.impact?.map(item => ({
    ...item,
    cls: accepted.has(item.planId || `solo-${item.repJobId}`) ? 'green' : classifyItem(item, warnThreshold),
  })) || [];
  const redItems   = classified.filter(i => i.cls === 'red');
  const amberItems = classified.filter(i => i.cls === 'amber');
  const greenCount = classified.filter(i => i.cls === 'green').length;
  const totalPlans = classified.length;

  const st = {
    page:   { background: DARK, minHeight: '100vh', fontFamily: 'system-ui, sans-serif', color: '#f0f2f5', padding: '32px 24px', maxWidth: '720px', margin: '0 auto' },
    back:   { background: 'none', border: 'none', color: MUTED, fontSize: '13px', cursor: 'pointer', padding: '0 0 20px', display: 'flex', alignItems: 'center', gap: '6px' },
    h1:     { fontSize: '22px', fontWeight: '700', marginBottom: '4px' },
    sub:    { fontSize: '13px', color: MUTED, marginBottom: '28px', lineHeight: '1.5' },
    card:   { background: '#151b23', border: `1px solid ${BORD}`, borderRadius: '12px', padding: '20px', marginBottom: '12px' },
    label:  { fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', display: 'block' },
    input:  { width: '100%', background: '#1e2329', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '9px 12px', color: '#f0f2f5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    btn:    { background: TEAL, border: 'none', borderRadius: '8px', padding: '9px 20px', color: '#0f1117', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    row:    { display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 0' },
    del:    { background: 'none', border: `1px solid #374151`, borderRadius: '6px', color: MUTED, fontSize: '11px', padding: '3px 8px', cursor: 'pointer', flexShrink: 0 },
    days:   { fontSize: '11px', color: MUTED, background: '#1e2329', borderRadius: '4px', padding: '2px 6px', flexShrink: 0 },
    sect:   { fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '20px 0 8px' },
    impCard: { background: '#151b23', border: `1px solid ${BORD}`, borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  };

  return (
    <div style={st.page}>
      <button style={st.back} onClick={goBack}>
        <i className="fa-solid fa-arrow-left" style={{ fontSize: '11px' }} /> Back
      </button>
      <div style={st.h1}>Non-Working Days</div>
      <div style={st.sub}>
        Mark company-specific closure dates — factory shutdowns, bridge days, extended breaks.
        These are treated the same as weekends and public holidays when scheduling.
        Adding a closure automatically pushes all future unstarted jobs forward.
      </div>

      {isDirector() && (
        <div style={st.card}>
          <form onSubmit={add}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={st.label}>From *</label>
                <input type="date" value={dateFrom} min={today}
                  onChange={e => handleFromChange(e.target.value)} style={st.input} required />
              </div>
              <div>
                <label style={st.label}>To *</label>
                <input type="date" value={dateTo} min={dateFrom || today}
                  onChange={e => setDateTo(e.target.value)} style={st.input} required />
              </div>
              <div>
                <label style={st.label}>Reason (optional)</label>
                <input type="text" value={reason} placeholder="e.g. Factory shutdown"
                  onChange={e => setReason(e.target.value)} style={st.input} maxLength={80} />
              </div>
            </div>
            {formError && <div style={{ fontSize: '12px', color: RED, marginBottom: '10px' }}>{formError}</div>}
            <button type="submit" style={{ ...st.btn, opacity: (saving || !dateFrom || !dateTo) ? 0.5 : 1 }}
              disabled={saving || !dateFrom || !dateTo}>
              {saving ? 'Saving & rescheduling…' : 'Add closure'}
            </button>
          </form>
        </div>
      )}

      {/* ── Impact report ──────────────────────────────────────────────────── */}
      {impactResult && (
        <div style={st.impCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>
                {impactResult.lostDays === 0
                  ? 'No working days affected'
                  : `${totalPlans} production run${totalPlans !== 1 ? 's' : ''} shifted forward ${impactResult.lostDays} working day${impactResult.lostDays !== 1 ? 's' : ''}`}
              </div>
              {impactResult.lostDays > 0 && totalPlans > 0 && (
                <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
                  Review the impact below and handle any deadline conflicts.
                </div>
              )}
            </div>
            <button onClick={() => setImpactResult(null)}
              style={{ background: 'none', border: 'none', color: MUTED, fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 16px' }}>×</button>
          </div>

          {impactResult.lostDays > 0 && totalPlans > 0 && (
            <>
              {/* Threshold control */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#1e2329', borderRadius: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', color: MUTED }}>Flag amber if finishing within</span>
                <input type="number" value={warnThreshold} min={0} max={60}
                  onChange={e => setWarnThreshold(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{ width: '52px', background: '#151b23', border: `1px solid ${BORD}`, borderRadius: '6px', padding: '4px 8px', color: '#f0f2f5', fontSize: '13px', textAlign: 'center', outline: 'none' }} />
                <span style={{ fontSize: '12px', color: MUTED }}>days of deadline</span>
              </div>

              {/* Red */}
              {redItems.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: RED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: RED, display: 'inline-block' }} />
                    Missing deadline — {redItems.length} run{redItems.length !== 1 ? 's' : ''}
                  </div>
                  {redItems.map(item => (
                    <ImpactRow key={item.planId || `solo-${item.repJobId}`}
                      item={item} cls="red" onAccept={acceptItem} navigate={navigate} />
                  ))}
                </div>
              )}

              {/* Amber */}
              {amberItems.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: AMBER, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: AMBER, display: 'inline-block' }} />
                    Reduced buffer — {amberItems.length} run{amberItems.length !== 1 ? 's' : ''}
                  </div>
                  {amberItems.map(item => (
                    <ImpactRow key={item.planId || `solo-${item.repJobId}`}
                      item={item} cls="amber" onAccept={acceptItem} navigate={navigate} />
                  ))}
                </div>
              )}

              {/* Green */}
              {greenCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 0', color: MUTED, fontSize: '13px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: GREEN, display: 'inline-block', flexShrink: 0 }} />
                  {greenCount} run{greenCount !== 1 ? 's' : ''} finish comfortably on time — no action needed
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Closure list ───────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ color: MUTED, fontSize: '13px', padding: '20px 0' }}>Loading…</div>
      ) : closures.length === 0 ? (
        <div style={{ color: MUTED, fontSize: '13px', padding: '20px 0' }}>No custom closures defined.</div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <>
              <div style={st.sect}>Upcoming</div>
              <div style={{ ...st.card, padding: '4px 16px' }}>
                {upcoming.map((c, i) => (
                  <div key={c.id} style={{ ...st.row, borderBottom: i < upcoming.length - 1 ? `1px solid ${BORD}` : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>{fmtRangeLabel(c)}</div>
                      {c.reason && <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{c.reason}</div>}
                    </div>
                    <span style={st.days}>{dayCount(c) === 1 ? '1 day' : `${dayCount(c)} days`}</span>
                    {isDirector() && (
                      <button style={st.del} onClick={() => api.delete(`/api/v1/closures/${c.id}`).then(load)}>Remove</button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {past.length > 0 && (
            <>
              <div style={st.sect}>Past</div>
              <div style={{ ...st.card, padding: '4px 16px', opacity: 0.5 }}>
                {past.map((c, i) => (
                  <div key={c.id} style={{ ...st.row, borderBottom: i < past.length - 1 ? `1px solid ${BORD}` : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>{fmtRangeLabel(c)}</div>
                      {c.reason && <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{c.reason}</div>}
                    </div>
                    <span style={st.days}>{dayCount(c) === 1 ? '1 day' : `${dayCount(c)} days`}</span>
                    {isDirector() && (
                      <button style={st.del} onClick={() => api.delete(`/api/v1/closures/${c.id}`).then(load)}>Remove</button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
